import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';

import type { InvoiceTemplate } from '~/api/invoiceTemplates/types';

import { useClientsWithTrashed } from '~/api/clients';
import { useInvoiceTemplate } from '~/api/invoiceTemplates';
import { AlertDialog, Container, Header, InfoBlock, Menu, Redirect, Separator, StatBlock, Text, Title } from '~/components';
import { ClientCard, CopyrightCard, DocumentLines } from '~/features/documents';
import { InvoicesTable } from '~/pages/InvoicesCreditnotes/components/InvoicesTable/InvoicesTable';
import { routes } from '~/providers/RouterProvider/router.routes';
import { clsx } from '~/styles/utils';

import type { loader } from './InvoiceTemplateDetail.loader';

import { ConfirmDeleteInvoiceTemplateDialog } from '../components/ConfirmDeleteInvoiceTemplateDialog';
import {
  useOnCreateInvoiceFromTemplate,
  useOnDeleteInvoiceTemplate,
  useOnEditInvoiceTemplate,
  useOnPauseInvoiceTemplate,
  useOnUnpauseInvoiceTemplate,
} from '../invoiceTemplates.hooks';
import {
  advantageStyle,
  containerStyle,
  descriptionStyle,
  sectionStyle,
  sectionTitleStyle,
  summaryLayoutStyles,
  summaryStyle,
  termsStyle,
  wrapperStyle,
} from './InvoiceTemplateDetail.css';
import { InvoiceTemplateStatusCard } from './InvoiceTemplateStatusCard';

export const InvoiceTemplateDetailGuard = () => {
  const loaderData = useLoaderData<typeof loader>();
  const { data: invoiceTemplate } = useInvoiceTemplate(loaderData.id);

  if (!invoiceTemplate) {
    return <Redirect to={routes.invoiceTemplates} />;
  }

  return <InvoiceTemplateDetail invoiceTemplate={invoiceTemplate} />;
};

type Props = {
  invoiceTemplate: InvoiceTemplate;
};

const InvoiceTemplateDetail = ({ invoiceTemplate }: Props) => {
  const { data: clients } = useClientsWithTrashed();
  const client = clients.find(({ id }) => id === invoiceTemplate.clientId) ?? null;

  const { onEdit } = useOnEditInvoiceTemplate(invoiceTemplate);
  const { isPending: isCreateInvoicePending, onCreateInvoiceFromTemplate } = useOnCreateInvoiceFromTemplate(invoiceTemplate);
  const { isPending: isPausePending, onPause } = useOnPauseInvoiceTemplate(invoiceTemplate);
  const { isPending: isUnpausePending, onUnpause } = useOnUnpauseInvoiceTemplate(invoiceTemplate);
  const {
    confirmDelete,
    deleteHandle,
    isPending: isDeletePending,
    onDelete,
  } = useOnDeleteInvoiceTemplate(invoiceTemplate, { redirect: true });

  const { t } = useTranslation(['common', 'invoices', 'invoiceTemplates']);

  return (
    <>
      <Header.Root>
        <Header.Content backTo={routes.invoiceTemplates} backToMobileOnly title={invoiceTemplate.title} />

        <Header.Actions>
          <Header.Action emphasis="secondary" icon="Edit" onClick={onEdit}>
            {t('invoiceTemplates:actions.edit')}
          </Header.Action>

          <Header.Action icon="Add" loading={isCreateInvoicePending} onClick={onCreateInvoiceFromTemplate}>
            {t('invoiceTemplates:actions.createInvoice')}
          </Header.Action>

          <Menu.Root>
            <Menu.Trigger
              render={
                <Header.Action
                  aria-label={t('common:actions.openMenu')}
                  emphasis="tertiary"
                  icon="MoreVert"
                  loading={isPausePending || isUnpausePending || isDeletePending}
                />
              }
            />

            <Menu.List>
              {invoiceTemplate.intervalStatus === 'Live' && (
                <Menu.Item icon="Pause" onClick={onPause}>
                  {t('invoiceTemplates:actions.pause')}
                </Menu.Item>
              )}

              {invoiceTemplate.intervalStatus === 'Paused' && (
                <Menu.Item icon="Play" onClick={onUnpause}>
                  {t('invoiceTemplates:actions.unpause')}
                </Menu.Item>
              )}

              <Menu.Item icon="Trash" onClick={onDelete}>
                {t('invoiceTemplates:actions.delete')}
              </Menu.Item>
            </Menu.List>
          </Menu.Root>
        </Header.Actions>
      </Header.Root>

      <Container className={containerStyle}>
        <div className={wrapperStyle}>
          <div className={clsx(summaryStyle, summaryLayoutStyles[invoiceTemplate.type])}>
            {invoiceTemplate.type === 'Recurring' && <InvoiceTemplateStatusCard invoiceTemplate={invoiceTemplate} />}

            <dl className={advantageStyle}>
              <StatBlock
                illustration="Coins"
                label={t('invoiceTemplates:details.stats.totalAdvantage.label')}
                value={invoiceTemplate.totalAdvantage}
                variant="primary-light"
              />
            </dl>

            <InfoBlock className={descriptionStyle} label={t('invoices:fields.description.label')}>
              <Text>{invoiceTemplate.description ?? t('invoices:details.summary.content.noDescription')}</Text>
            </InfoBlock>
          </div>

          <div className={termsStyle}>
            <InfoBlock label={t('invoices:details.copyright.label')}>
              <CopyrightCard
                conditionId={invoiceTemplate.conditionId}
                customAgreementId={invoiceTemplate.customAgreementId}
                customConditionId={invoiceTemplate.customConditionId}
                specialConditions={invoiceTemplate.specialConditions}
              />
            </InfoBlock>

            <InfoBlock label={t('invoices:fields.clientId.label')}>
              <ClientCard clientId={invoiceTemplate.clientId} contactId={invoiceTemplate.contactId} />
            </InfoBlock>
          </div>

          <Separator />

          <DocumentLines calculationData={invoiceTemplate.calculationData} client={client} lines={invoiceTemplate.lines} />

          <Separator />

          <section className={sectionStyle}>
            <Title as="h2" className={sectionTitleStyle} styleAs="h3">
              {t('invoiceTemplates:details.invoices.titleWithCount', { amount: invoiceTemplate.totalInvoices })}
            </Title>

            <div>
              <InvoicesTable invoiceTemplateId={invoiceTemplate.id} />
            </div>
          </section>
        </div>
      </Container>

      <AlertDialog.Root handle={deleteHandle}>
        <ConfirmDeleteInvoiceTemplateDialog loading={isDeletePending} onConfirm={confirmDelete} />
      </AlertDialog.Root>
    </>
  );
};
